import { useCallback } from 'react';

import { useUpdateUnsignedOrder, useUpdateSignedOrder } from 'api/requests';
import { OrderEntity, OrderStatusType } from 'api/types/entity';
import { useServerErrorNotify } from 'hooks';
import { FormSubmit } from 'libs/form';
import { useTranslation } from 'libs/i18n';
import { useNavigate } from 'libs/navigation';
import { useNotify } from 'libs/notify';

import { OrderFormType } from '../forms/orderForm';
import {
  getSignedOrderSubmitData,
  getUnsignedOrderSubmitData,
} from '../helpers';

export const useUpdateOrder = (order: OrderEntity | null) => {
  const { params } = useNavigate<{ orderId: string }>();
  const { t } = useTranslation();

  const notify = useNotify();

  const {
    updateSignedOrder,
    loading: submitSignedLoading,
    error: submitSignedError,
  } = useUpdateSignedOrder();

  const {
    updateUnsignedOrder,
    loading: submitUnsignedLoading,
    error: submitUnsignedError,
  } = useUpdateUnsignedOrder();

  // Submit handler for the order form
  const onSubmit: FormSubmit<OrderFormType> = useCallback(
    async (values, helpers) => {
      let res: OrderEntity | null;
      if (order?.status === OrderStatusType.AwaitingSigning) {
        res = await updateUnsignedOrder(
          params.orderId!,
          order.versionNumber!,
          getUnsignedOrderSubmitData(order, values)
        );
      } else {
        res = await updateSignedOrder(
          params.orderId!,
          order?.versionNumber!,
          getSignedOrderSubmitData(values)
        );
      }

      if (res) {
        notify.info(t('common.successSave'));
        helpers.setReadOnly(true);
      }
    },
    [order, notify, t, params.orderId, updateUnsignedOrder, updateSignedOrder]
  );

  useServerErrorNotify(submitSignedError || submitUnsignedError);

  return {
    submitLoading: submitSignedLoading || submitUnsignedLoading,
    onSubmit,
  };
};
